import React, { useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  Collapse,
  useTheme,
  Grid,
} from "@mui/material";
import AuctionEdgeForm from "./services/AuctionEdgeForm";
import CRSForm from "./services/CRSForm";
import DealersCircleForm from "./services/DealersCircleForm";
import HomenetForm from "./services/HomenetForm";
import OctaneForm from "./services/OctaneForm";
import SIAForm from "./services/SIAForm";
import VincueForm from "./services/VincueForm";
import XciraForm from "./services/XciraForm";
import { getFormSectionStyles } from "../formSectionStyles";

const SERVICE_COMPONENTS: Record<string, React.FC<any>> = {
  auctionEdge: AuctionEdgeForm,
  crs: CRSForm,
  dealersCircle: DealersCircleForm,
  homenet: HomenetForm,
  octane: OctaneForm,
  sia: SIAForm,
  vincue: VincueForm,
  xcira: XciraForm,
};

interface AssetSyndicationFormProps {
  config: any;
  handleChange: (path: string, value: any) => void;
}

const SERVICE_OPTIONS = Object.keys(SERVICE_COMPONENTS);

const AssetSyndicationForm: React.FC<AssetSyndicationFormProps> = ({
  config,
  handleChange,
}) => {
  const theme = useTheme();
  const styles = getFormSectionStyles(theme);
  const cachedConfigsRef = useRef<Record<string, any>>({});

  const enabledServices = useMemo(() => {
    return SERVICE_OPTIONS.filter(
      (s) => config[`assetSyndication_${s}_serviceConfig`]
    );
  }, [config]);

  const toggleService = (service: string, enabled: boolean) => {
    const serviceKey = `assetSyndication_${service}_serviceConfig`;
    const stepFlag = `assetSyndication_${service}_serviceFlag`;

    let flowSteps: string[] = [];
    const fc = config.flowControl_assetSyndication_steps;
    if (Array.isArray(fc)) flowSteps = [...fc];
    else if (typeof fc === "object" && fc !== null)
      flowSteps = Object.values(fc).map(String);

    if (enabled) {
      if (!flowSteps.includes(stepFlag)) flowSteps.push(stepFlag);
      const restored = cachedConfigsRef.current[service] || {};
      handleChange(serviceKey, restored);
      cachedConfigsRef.current[service] = restored;
    } else {
      cachedConfigsRef.current[service] = config[serviceKey];
      flowSteps = flowSteps.filter((s) => s !== stepFlag);
      handleChange(serviceKey, undefined);
    }

    handleChange("flowControl_assetSyndication_steps", flowSteps);
  };

  return (
    <Paper
      sx={styles.paper}
    >
      <Typography
        variant="h6"
        sx={styles.title}
      >
        Asset Syndication Services
      </Typography>

      {/* ---------- TOP ROW OF CHECKBOXES ---------- */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {SERVICE_OPTIONS.map((service) => {
          const enabled = enabledServices.includes(service);
          return (
            <Grid item xs="auto" key={service}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={enabled}
                    onChange={(e) => toggleService(service, e.target.checked)}
                    sx={styles.checkbox}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {service.charAt(0).toUpperCase() + service.slice(1)}
                  </Typography>
                }
              />
            </Grid>
          );
        })}
      </Grid>

      {/* ---------- STACKED CHILD COMPONENTS ---------- */}
      {SERVICE_OPTIONS.map((service) => {
        const Component = SERVICE_COMPONENTS[service];
        const serviceKey = `assetSyndication_${service}_serviceConfig`;
        const enabled = enabledServices.includes(service);

        return (
          <Collapse in={enabled} unmountOnExit key={service}>
            <Box
              sx={styles.nestedSection}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: styles.accent,
                }}
              >
                {service.charAt(0).toUpperCase() + service.slice(1)} Settings
              </Typography>

              <Component
                config={config[serviceKey] || {}}
                onChange={(field: string, value: any) =>
                  handleChange(`${serviceKey}.${field}`, value)
                }
              />
            </Box>
          </Collapse>
        );
      })}
    </Paper>
  );
};

export default AssetSyndicationForm;
